import { Node, mergeAttributes } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import FigureNodeView from './FigureNodeView.vue';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        figure: {
            /** Insert a figure (image with caption) at the current position. */
            insertFigure: (options: {
                assetId: string;
                alt?: string;
                caption?: string;
                width?: number | null;
            }) => ReturnType;
        };
    }
}

/**
 * Block atom node that wraps a project image asset with an auto-numbered caption.
 *
 * Stored JSON attrs:
 *   - figureId : crypto.randomUUID() — unique per insertion, used for live counter
 *   - assetId  : ProjectAsset.id — used to resolve the serve URL from injected assets list
 *   - alt      : img alt text
 *   - caption  : editable caption text (shown as "Figure N : <caption>")
 *   - width    : display width in pixels (null = 100%)
 */
export const FigureExtension = Node.create({
    name: 'figure',

    group: 'block',

    atom: true,

    draggable: true,

    addAttributes() {
        return {
            figureId: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-figure-id'),
                renderHTML: (attrs) => ({ 'data-figure-id': attrs.figureId }),
            },
            assetId: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-asset-id'),
                renderHTML: (attrs) => ({ 'data-asset-id': attrs.assetId }),
            },
            alt: {
                default: '',
                parseHTML: (el) =>
                    el.querySelector('img')?.getAttribute('alt') ?? '',
                renderHTML: () => ({}),
            },
            caption: {
                default: '',
                parseHTML: (el) =>
                    el.querySelector('[data-caption]')?.textContent ?? '',
                renderHTML: () => ({}),
            },
            width: {
                default: null,
                parseHTML: (el) => {
                    const w = el.getAttribute('data-width');

                    return w ? parseInt(w, 10) : null;
                },
                renderHTML: (attrs) =>
                    attrs.width ? { 'data-width': String(attrs.width) } : {},
            },
        };
    },

    parseHTML() {
        return [{ tag: 'figure[data-figure-id]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['figure', mergeAttributes(HTMLAttributes), 0];
    },

    addNodeView() {
        return VueNodeViewRenderer(FigureNodeView);
    },

    addCommands() {
        return {
            insertFigure:
                ({ assetId, alt = '', caption = '', width = null }) =>
                ({ commands }) => {
                    const figureId =
                        typeof crypto !== 'undefined' && crypto.randomUUID
                            ? crypto.randomUUID()
                            : Math.random().toString(36).slice(2);

                    return commands.insertContent({
                        type: 'figure',
                        attrs: { figureId, assetId, alt, caption, width },
                    });
                },
        };
    },
});
